import React, { useMemo } from 'react';
import type { ReactNode } from 'react';

import { Tag } from '../Tag';
import type { AuthProps } from '../../auth';
import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';

export type StatusTone = 'default' | 'success' | 'warning' | 'error' | 'processing';

export interface StatusConfig {
    label: ReactNode;
    tone: StatusTone;
    icon?: ReactNode;
}

export interface StatusTagProps extends AuthProps, Styleable {
    /** The raw status value from the caller's domain (e.g. `'published'`, `'revoked'`). */
    status: string;
    /** Caller-owned map of every status this screen knows about to its display config. */
    config: Record<string, StatusConfig>;
    /** Used when `status` is absent from `config`, instead of the raw-string default. */
    fallback?: StatusConfig;
}

/**
 * Tier-1 presentational primitive: renders a colored status pill from a
 * caller-supplied `status` → `{ label, tone, icon }` map. Carries zero domain
 * vocabulary of its own — it never hardcodes what "published" or "revoked"
 * means, that mapping lives entirely in `config`, owned by the app screen.
 * An unknown `status` never throws or renders blank: it falls back to
 * `fallback` when supplied, otherwise to the raw `status` string with the
 * `default` tone. Composes the existing `Tag` primitive (rather than
 * re-wrapping antd's `Tag`) so it inherits the shared auth-guard contract
 * (`auth`/`authFallback`) and the one place antd `Tag` is themed, instead of
 * duplicating that wiring here.
 */
export function StatusTag({
    status,
    config,
    fallback,
    auth,
    authFallback,
    className,
    style,
}: StatusTagProps): React.ReactElement {
    const colors = useUIColors();

    const toneMap = useMemo(
        () => ({
            default: { bg: colors.codeBg, text: colors.textMuted, border: colors.border },
            success: { bg: colors.successLight, text: colors.success, border: colors.success },
            warning: { bg: colors.warningBg, text: colors.warning, border: colors.warning },
            error: { bg: colors.errorBg, text: colors.error, border: colors.error },
            processing: { bg: colors.infoBg, text: colors.primary, border: colors.primary },
        }),
        [colors]
    );

    const resolved: StatusConfig = config[status] ?? fallback ?? { label: status, tone: 'default' };
    const toneSet = toneMap[resolved.tone];

    return (
        <Tag
            className={cx(className)}
            auth={auth}
            authFallback={authFallback}
            icon={resolved.icon}
            style={{
                backgroundColor: toneSet.bg,
                color: toneSet.text,
                borderColor: toneSet.border,

                ...style,
            }}
        >
            {resolved.label}
        </Tag>
    );
}

export default StatusTag;
